import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Form as FormikForm, Formik } from 'formik';
import { Box, Button, Unstable_Grid2 as Grid } from '@mui/material';

import { MinimalUserProfile, S3ObjectInput, useUpdateUserProfileMutation } from 'api';
import { AspectRatios } from 'types/app';
import {
  IUploader,
  UploadDialog,
  Uploader,
  UploadInput,
  UserAvatar,
  ValidatedTextFieldNext,
} from 'components';
import { useBootstrapQueryKey, useCurrentUser, useNotify } from 'hooks';

import { jobTitleSchema } from '../validations';

const SetupInformation: FC = () => {
  const currentUser = useCurrentUser();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const bootstrapQueryKey = useBootstrapQueryKey();
  const { t } = useTranslation();

  const initialValues = {
    job_title: currentUser.job_title || '',
    image: currentUser.image,
  };

  const { mutate } = useUpdateUserProfileMutation({
    onError: notify.mutationError,
    onSuccess: () => notify.info({ message: t('components:savedSuccessfully') }),
    onSettled: () => {
      void queryClient.refetchQueries(bootstrapQueryKey);
    },
  });

  function handleSaveImage(image: S3ObjectInput) {
    mutate({ data: { id: currentUser.id, image } });
  }

  function handleSaveJobTitle(values: { job_title: string }) {
    mutate({ data: { id: currentUser.id, ...values } });
  }

  return (
    <Fragment>
      <Box marginTop={3}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSaveJobTitle}
          validationSchema={jobTitleSchema}
        >
          {({ values, setFieldValue }) => (
            <Uploader
              onSave={image => {
                setFieldValue('image', image);
                handleSaveImage(image);
              }}
            >
              {(uploaderProps: IUploader) => (
                <Fragment>
                  <Grid container wrap="nowrap" spacing={2} alignItems="center">
                    <Grid>
                      <UploadInput onUpload={uploaderProps.onUpload}>
                        <UserAvatar user={values as unknown as MinimalUserProfile} size={64} />
                      </UploadInput>
                    </Grid>
                    <Grid>
                      <UploadInput onUpload={uploaderProps.onUpload}>
                        <Button color="primary" component="span" variant="contained" fullWidth>
                          {t('setup:setupProfileProfileImage')}
                        </Button>
                      </UploadInput>
                    </Grid>
                  </Grid>
                  <UploadDialog
                    i18nKeyTitle="user:yourProfileImage"
                    i18nKeyReplace="components:replaceImage"
                    aspectRatio={AspectRatios.square}
                    {...uploaderProps}
                  />
                </Fragment>
              )}
            </Uploader>
          )}
        </Formik>
      </Box>
      <Box marginTop={3}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSaveJobTitle}
          validationSchema={jobTitleSchema}
        >
          <FormikForm>
            <ValidatedTextFieldNext
              fullWidth
              name="job_title"
              placeholder={t('setup:setupProfileTitle')}
              InputLabelProps={{ shrink: true }}
            />
          </FormikForm>
        </Formik>
      </Box>
    </Fragment>
  );
};

export default SetupInformation;
